import { utils } from '@digital-commerce/shared-utils'

import {
  pipe,
  prop,
  cond,
  allPass,
  includes,
  T,
  always,
  propOr,
  filter,
  complement,
  pathOr,
  propEq,
  find,
  ifElse,
  isNil,
  anyPass,
  isEmpty,
  curry,
  path,
  reject,
  groupBy,
  without,
  intersection,
  map
} from 'ramda'

import { filterExistingActions, isMonthlyRecurringAmountNotZero } from './cart'
import { formatCurrency } from './currency'

const { hasContractTerm, findMonthlyRecurring } = utils.cart

export const CONSTANTS = {
  DELIVERY_METHOD_DISPLAY_NAME: 'Delivery Method',
  QUICK_CONNECT: {
    VALUE: '9157950816213373078',
    DISPLAY_VALUE: 'Software'
  },
  SELF_INSTALL_DISPLAY_VALUE: 'Self-Install',
  INSTALL_TYPES: {
    SELF_INSTALL: 'SELF_INSTALL',
    PRO_INSTALL: 'PRO_INSTALL',
    QUICK_CONNECT: 'QUICK_CONNECT'
  },
  DELIVERY_METHOD_ID: '9157950816213373074',
  PRO_INSTALL_ID: '9157950816213373076',
  FIFA_IRPC: {
    FIFA_IRPC_CHANGE_URL: '/my-telus/internet/manage'
  }
}

export const getSubItemSortingWeight = pipe(
  prop('tags'),
  cond([
    [allPass([includes('tv'), includes('premium')]), always(1)],
    [allPass([includes('tv'), includes('themepack')]), always(2)],
    [T, always(3)]
  ])
)

export const getTVExtras = pipe(
  propOr([], 'items'),
  filter(
    pipe(
      prop('tags'),
      allPass([includes('tv'), complement(includes('hardware')), complement(includes('addon'))])
    )
  ),
  filter(isMonthlyRecurringAmountNotZero)
)

const isSelfInstall = pipe(
  pathOr([], ['productOffering', 'characteristics']),
  find(propEq('displayName', CONSTANTS.DELIVERY_METHOD_DISPLAY_NAME)),
  ifElse(isNil, () => false, propEq('displayValue', CONSTANTS.SELF_INSTALL_DISPLAY_VALUE))
)

const isQuickConnect = pipe(
  pathOr([], ['productOffering', 'characteristics']),
  find(propEq('displayName', CONSTANTS.DELIVERY_METHOD_DISPLAY_NAME)),
  ifElse(
    isNil,
    () => false,
    anyPass([
      propEq('value', CONSTANTS.QUICK_CONNECT.VALUE),
      propEq('displayValue', CONSTANTS.QUICK_CONNECT.DISPLAY_VALUE)
    ])
  )
)

export const getInstallType = ifElse(
  isSelfInstall,
  () => CONSTANTS.INSTALL_TYPES.SELF_INSTALL,
  ifElse(
    isQuickConnect,
    () => CONSTANTS.INSTALL_TYPES.QUICK_CONNECT,
    () => CONSTANTS.INSTALL_TYPES.PRO_INSTALL
  )
)
const hasFinancingAddons = pipe(
  filter(pipe(prop('tags'), allPass([includes('financing'), includes('addon')]))),
  complement(isEmpty)
)

export const getInternetAddons = pipe(
  propOr([], 'items'),
  filter(pipe(prop('tags'), allPass([includes('internet'), includes('addon')])))
)

export const hasInternetFinanceAddons = pipe(getInternetAddons, hasFinancingAddons)

export const isSmartHubService = curry((cart) => {
  return pipe(prop('tags'), allPass([includes('service-plan'), includes('smart-hub')]))(cart)
})

export const hasODUUnitInSmartHub = pipe(
  propOr([], 'items'),
  find(pipe(propOr([], 'tags'), anyPass([includes('odu1'), includes('odu2')])))
)

export const getLegalFootnoteKeys = (service, addons, isBusinessCart = false) => {
  const hasEasyPaymentAddons = hasFinancingAddons(addons)
  const hasContract = hasContractTerm(service)
  const isSmartHub = isSmartHubService(service)
  const hasODUSmartHub = hasODUUnitInSmartHub(service)
  const isSmarthubODU = isSmartHub && hasODUSmartHub
  const isSmarthubIDU = isSmartHub && !hasODUSmartHub
  const hasIDU2YearTerm = hasContract && isSmarthubIDU && !isBusinessCart
  const hasODU2YearTerm = hasContract && isSmarthubODU && !isBusinessCart
  const legalFootNoteKeys = {
    easyPaymentcancellationFeeSlug: hasEasyPaymentAddons,
    cancellationFeeLegalSlug:
      !hasEasyPaymentAddons && hasContract && !isBusinessCart && !isSmartHub,
    sbsCancellationFeeLegalSlug: !hasEasyPaymentAddons && hasContract && isBusinessCart,
    noTermcancellationFeeLegalSlug:
      !hasEasyPaymentAddons && !hasContract && !isBusinessCart && !isSmartHub,
    sbsNoTermCancellationFeeLegalSlug: !hasEasyPaymentAddons && !hasContract && isBusinessCart,
    rentalEquipmentSlug: hasContract && !isSmartHub,
    hsSmartHubODU2YearTermNewCustomersSlug: hasODU2YearTerm,
    hsSmartHubODU2YearTermOfferChangeSlug: hasODU2YearTerm,
    hsSmartHubIDU2YearTermOfferChangeSlug: hasIDU2YearTerm,
    hsSmartHub2YearTermViolationSlug: hasIDU2YearTerm || hasODU2YearTerm,
    hsSmartHub2YearTermSpeedSlug: hasIDU2YearTerm || hasODU2YearTerm,
    hsSmartHubIDU2YearTermDevicesSlug: hasIDU2YearTerm,
    hsSmartHubODU2YearTermDevicesSlug: hasODU2YearTerm
  }

  return Object.keys(legalFootNoteKeys).filter((key) => legalFootNoteKeys[key])
}

export const getIRPCMonthlyDiscounts = pipe(
  path(['itemPrice', 'recurring']),
  findMonthlyRecurring,
  propOr([], 'alterations'),
  reject(propEq('action', 'delete')),
  groupBy((alteration) => {
    const duration = alteration.remainingDuration ?? alteration.recurringDuration
    return duration === 0 || duration > 5 ? 'dueMonthly' : 'otherCharges'
  })
)

/* EPP and Affiliates utils - start */

export const isHSEppDiscount = (discount) => discount?.name?.startsWith('EPP')

export const getHSEppDiscount = (service) => {
  const monthlyRecurring = findMonthlyRecurring(service?.itemPrice?.recurring)
  return monthlyRecurring?.alterations?.find((alteration) => isHSEppDiscount(alteration))
}

export const formatHSEppDiscount = (eppOrAffiliateDiscount, translate, lang, isAffiliates) => ({
  id: eppOrAffiliateDiscount.promotionId,
  name: translate(
    isAffiliates ? 'priceBreakdown.affiliateDiscountLabel' : 'priceBreakdown.eppDiscountLabel',
    {
      replacements: {
        numberOfMonths: eppOrAffiliateDiscount.recurringDuration,
        plural: eppOrAffiliateDiscount.recurringDuration > 1 ? 's' : '' // used for EN only
      }
    }
  ),
  price: `-${formatCurrency(eppOrAffiliateDiscount.amount, lang)}/mo.`,
  prefixIcon: 'DEALS'
})

/* EPP and Affiliates utils - end */

/**
 * Given a service (i.e. Internet, OptikTV), remove the discounts specified
 * by discountsToRemove from the service and return the service without
 * these discounts attached.
 * This is used to remove discounts that will be displayed as separate line
 * items and should not be included in the remaining discount calculations
 * for the service.
 * @param {object} service Service item from the cart.
 * @param {array} discountsToRemove Accepts an array of objects, where the objects are the discounts to be removed.
 * @param {boolean} shouldFilterExistingActions If true, discounts with action: "EXISTING" will also be removed from the service.
 * @returns Service item from the cart with the discountsToRemove filtered out, and discounts with action: "EXISTING" filtered out if shouldFilterExistingActions was true.
 */
export const getServiceWithoutSpecifiedDiscounts = (
  service,
  discountsToRemove = [],
  shouldFilterExistingActions = false
) => {
  let monthlyDiscounts = findMonthlyRecurring(service?.itemPrice?.recurring)?.alterations

  if (!monthlyDiscounts) return service

  if (shouldFilterExistingActions) {
    monthlyDiscounts = filterExistingActions(monthlyDiscounts)
  }
  const filteredMonthlyDiscounts = without(discountsToRemove, monthlyDiscounts)

  const filteredRecurrings = pipe(
    path(['itemPrice', 'recurring']),
    map((recurring) => {
      const recc = { ...recurring }
      const recAlts = intersection(recc.alterations, filteredMonthlyDiscounts)
      return { ...recurring, alterations: [...recAlts] }
    })
  )(service)

  const result = {
    ...service,
    itemPrice: {
      ...service.itemPrice,
      recurring: filteredRecurrings
    }
  }
  return result
}
